import { Button, Icon, Input, Popover, Spinner, Table } from "@thetreep/ui";
import {
  CellContext,
  ColumnDef,
  PaginationState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DAPImport } from "@pages/admin/daps/DAPImport";
import theme from "@theme";
import { Field } from "./Field";
import { FieldValues } from "react-hook-form";
import Select from "./Select";
import { PlaceInput } from "./PlaceInput";
import { Place, PlaceType } from "@gen/place/v1/place_pb";

function ThreeDotsButton({ actions }: { actions: Action[] }) {
  const popover = useRef<{ close: () => void } | null>(null);
  const handleClick = (action: Action) => {
    action.do();
    popover.current?.close();
  };
  const menu = (
    <ul className="bg-white p-2 m-0 rounded-md border border-border">
      {actions.map((action) => (
        <li
          className="flex items-center justify-start gap-2 
                    text-text-strong text-sm px-4 py-2 cursor-pointer 
                    list-none text-center rounded-md hover:bg-neutral-100"
          key={action.label}
          onClick={() => handleClick(action)}
        >
          <Icon src={action.icon} color={theme.colors.text.strong} />
          <span>{action.label}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <Popover
      placement="bottomRight"
      content={({ close }) => {
        popover.current = { close };
        return menu;
      }}
    >
      <div className="cursor-pointer flex">
        <Icon src="more_2_line" />
      </div>
    </Popover>
  );
}

interface Action {
  label: string;
  icon: string;
  do: () => void;
}

type RowActions<T> = (row: T) => Action[];

interface TableViewProps<
  T extends FieldValues,
  F extends Partial<Record<keyof T, unknown>> = Partial<
    Record<keyof T, unknown>
  >,
> {
  items?: T[];
  fields: Field<T>[];
  title: string;
  pagination: PaginationState;
  sorting: SortingState;
  filters?: F;
  totalItems: number;
  topActions?: Action[];
  rowActions?: RowActions<T>;
  setPagination: (s: Updater<PaginationState>) => void;
  setSorting: (s: Updater<SortingState>) => void;
  setFilters?: (s: F) => void;
  refresh?: () => void;
  isLoadingItems: boolean;
  // le Table de @thetreep/ui ne relit jamais paginationInit apres le montage:
  // changer cette cle le remonte pour resynchroniser son pied de page
  tableKey?: number;
}

export function TableView<
  T extends FieldValues,
  F extends Partial<Record<keyof T, unknown>> = Partial<
    Record<keyof T, unknown>
  >,
>({
  fields,
  items,
  title,
  pagination,
  sorting,
  filters,
  totalItems,
  topActions,
  rowActions,
  setPagination,
  setSorting,
  setFilters,
  refresh,
  isLoadingItems,
  tableKey,
}: TableViewProps<T, F>) {
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { t } = useTranslation();

  const handlePagination = (updater: Updater<PaginationState>) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    if (
      next.pageIndex === pagination.pageIndex &&
      next.pageSize === pagination.pageSize
    ) {
      return;
    }
    setPagination(next);
  };
  const handleSorting = (updater: Updater<SortingState>) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    const a = next[0];
    const b = sorting[0];
    if (a?.id === b?.id && a?.desc === b?.desc) {
      return;
    }
    setSorting(next);
  };

  if (!items) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  function getLabel(field: Field<T>): string {
    if (field.header) {
      return field.header;
    }
    if (field.label) {
      return field.label;
    }
    if (field.key) {
      return t(field.key as string);
    }
    return "";
  }

  const getColumns: (t: (s: string) => string) => ColumnDef<T>[] = () => {
    return [
      ...fields
        .filter((field) => field.key)
        .map((field) => ({
          accessorKey: field.key,
          cell: (info: CellContext<T, unknown>) => {
            let displayValue = info.getValue();
            if (
              displayValue === undefined ||
              (Array.isArray(displayValue) && displayValue.length === 0)
            ) {
              return "/";
            }
            if (field.display) {
              displayValue = field.display(displayValue as T[keyof T]);
            } else if (field.options) {
              if (Array.isArray(displayValue)) {
                displayValue = displayValue
                  .map((v) => field.options?.find((o) => o.value === v)?.label)
                  .join(", ");
              } else {
                displayValue =
                  field.options.find((o) => o.value === displayValue)?.label ||
                  (displayValue as ReactNode);
              }
            }
            return displayValue;
          },
          header: getLabel(field),
          enableSorting: !!field.sortable,
        })),
      ...(rowActions && rowActions.length > 0
        ? [
            {
              id: "actions",
              cell: (info: CellContext<T, unknown>) =>
                rowActions(info.row.original).length > 0 && (
                  <ThreeDotsButton actions={rowActions(info.row.original)} />
                ),
              meta: {
                sticky: "right",
              },
              size: 22,
            },
          ]
        : []),
    ] as ColumnDef<T>[];
  };

  function getFilters() {
    if (!filters) {
      return;
    }

    return fields
      .filter((field) => field.filterable)
      .map((field) => {
        const value = filters[field.key];
        switch (field.type) {
          case "text":
          case "number":
            return (
              <div key={field.key as string} className="w-64 [&>div]:h-12">
                <Input
                  placeholder={getLabel(field)}
                  onChange={(e) => {
                    if (!setFilters) return;
                    const raw = e.target.value;
                    let value;
                    if (raw === "") {
                      value = undefined;
                    } else if (field.type === "number") {
                      value = Number(raw) || undefined;
                    } else {
                      value = raw;
                    }
                    setFilters({
                      ...filters,
                      [field.key as keyof T]: value,
                    });
                  }}
                />
              </div>
            );

          case "place":
            return (
              <div key={field.key as string} className="w-64 h-12">
                <PlaceInput
                  value={value as Place}
                  placeholder={getLabel(field)}
                  onChange={(e) =>
                    setFilters &&
                    setFilters({
                      ...filters,
                      [field.key]: e,
                    })
                  }
                  filter={[PlaceType.CITY]}
                />
              </div>
            );

          case "select-toggle":
          case "select":
            return (
              <div
                key={field.key as string}
                className="w-64 [&>div>div>div:first-child]:min-h-12"
              >
                <Select
                  multiple={field.multi}
                  search={field.search}
                  // I could find a workaround by my IQ is only 80
                  //oxlint-disable-next-line @typescript-eslint/no-explicit-any
                  options={(field.filterOptions ?? field.options ?? []) as any}
                  placeholder={getLabel(field)}
                  optional
                  value={value}
                  onChange={(e) =>
                    setFilters &&
                    setFilters({
                      ...filters,
                      [field.key]: e,
                    })
                  }
                />
              </div>
            );
        }
      });
  }

  return (
    <>
      <div className="w-full pt-12 md:pt-20 pb-6 flex flex-col gap-6">
        <div className="w-full flex justify-between">
          <span className="text-[28px] font-jakarta font-bold ">{title}</span>
          {topActions &&
            topActions.map((action) => (
              <Button
                key={action.label}
                data-testid="open-modal-import-dap-file-button"
                size="mini"
                colors={{
                  backgroundColor: theme.colors.cnam.blue.DEFAULT,
                  color: theme.colors.white,
                }}
                startEnhancer={
                  <Icon src={action.icon} color={theme.colors.white} />
                }
                onClick={() => action.do()}
              >
                {action.label}{" "}
              </Button>
            ))}
        </div>
        <div className="flex gap-2 flex-wrap">{getFilters()}</div>
        <div className="flex-1">
          <Table
            key={tableKey}
            columns={getColumns(t)}
            data={items}
            totalItems={totalItems}
            sorting={sorting}
            onSortingChange={handleSorting}
            paginationInit={pagination}
            onPaginationChange={handlePagination}
            isFetching={!items || isLoadingItems}
          />
        </div>
      </div>
      <DAPImport
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImport={() => (refresh ? refresh() : null)}
      />
    </>
  );
}
